import { JobFormOptions } from '../types/JobTypes';
import { useGetJobFormOptionsQuery } from '@/common/infrastructure/gql/generated/schema';
import { filterEquipmentSizes, filterEquipmentSizesByActiveEquipment } from '@/jobs/application/utils/equipmentSizeUtils';
import { getDefaultId } from '@/raptorjob/application/utils/erpSettings';
import { useMemo } from 'react';

export const useGetJobFormOptions = (skip: boolean) => {
  const { data, loading, error } = useGetJobFormOptionsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network'
  });

  const formOptions = data?.raptorJobSupportingDataQuery?.value as JobFormOptions | null | undefined;

  const availableEquipment = formOptions?.availableEquipment ?? [];
  const allEquipmentSizes = formOptions?.availableEquipmentSizes ?? [];

  // Deduplicated sizes without active-equipment filtering (used for certperm and similar flows)
  const allDeduplicatedEquipmentSizes = useMemo(() => filterEquipmentSizes(allEquipmentSizes), [allEquipmentSizes]);

  // Filter equipment sizes: deduplicate then filter by active equipment (used for job flows)
  const filteredEquipmentSizes = useMemo(() => {
    return filterEquipmentSizesByActiveEquipment(allDeduplicatedEquipmentSizes, availableEquipment);
  }, [allDeduplicatedEquipmentSizes, availableEquipment]);

  const defaultVendorId = getDefaultId(formOptions?.availableVendors ?? []);
  const defaultJobStatusId = getDefaultId(formOptions?.availableJobStatuses ?? []);
  const defaultJobTypeId = getDefaultId(formOptions?.availableJobTypes ?? []);

  return {
    formOptions,
    loading,
    error,

    availableCrewMembers: formOptions?.availableCrewMembers ?? [],
    availableCrewRoles: formOptions?.availableCrewRoles ?? [],
    availableCustomers: formOptions?.availableCustomers ?? [],
    availableDispatchLocations: formOptions?.availableDispatchLocations ?? [],
    availableDispatchers: formOptions?.availableDispatchers ?? [],
    availableEquipment,
    availableEquipmentLocations: formOptions?.availableEquipmentLocations ?? [],
    availableEquipmentSizes: filteredEquipmentSizes,
    allAvailableEquipmentSizes: allDeduplicatedEquipmentSizes,
    availableEquipmentTypes: formOptions?.availableEquipmentTypes ?? [],
    availableExtraSystems: formOptions?.availableExtraSystems ?? [],
    availableJobInformation: formOptions?.availableJobInformation ?? [],
    availableJobStatuses: formOptions?.availableJobStatuses ?? [],
    availableJobTypes: formOptions?.availableJobTypes ?? [],
    // availableJobSites: formOptions?.availableJobSites ?? [],
    availableSalesAgents: formOptions?.availableSalesAgents ?? [],
    availableSpecialFlags: formOptions?.availableSpecialFlags ?? [],
    availableVendors: formOptions?.availableVendors ?? [],
    defaultVendorId,
    defaultJobStatusId,
    defaultJobTypeId,
    requiredFields: formOptions?.requiredFields ?? [],
    uiBehaviorRules: formOptions?.uiBehaviorRules ?? []
  };
};
